import type { CSSProperties } from "react";

export type DataTableColWidth = {
  width: number;
  minWidth?: number;

  flex?: boolean;
};

export const DATA_TABLE_MIN_WIDTH_PX = 800;

export function colWidth(width: number, minWidth?: number): DataTableColWidth {
  return { width, minWidth: minWidth ?? width };
}

export function colFlex(minWidth: number, width?: number): DataTableColWidth {
  return { flex: true, minWidth, width: width ?? minWidth };
}

export function resolveColStyle(
  def?: DataTableColWidth,
): CSSProperties | undefined {
  if (!def) {
    return undefined;
  }

  const min = def.minWidth ?? def.width;

  if (def.flex) {
    return { width: "auto", minWidth: min };
  }

  return { width: def.width, minWidth: min };
}

export type DataTableColumnMeta = {
  colWidth?: DataTableColWidth;
  className?: string;
  noTruncate?: boolean;
  align?: "center";

  customSerial?: boolean;

  pin?: "left" | "right";
};

export type DataTablePinInfo = {
  side: "left" | "right";
  offset: number;

  edge: boolean;
};

function pinnedColumnWidth(def?: DataTableColWidth): number {
  if (!def) {
    return 0;
  }
  return def.width ?? def.minWidth ?? 0;
}

export function buildDataTablePinMap(
  columns: Array<{ id: string; meta?: unknown }>,
): Map<string, DataTablePinInfo> {
  const map = new Map<string, DataTablePinInfo>();

  let leftOffset = 0;
  let lastLeftId: string | null = null;
  for (const column of columns) {
    const meta = getColumnMeta(column.meta);
    if (meta?.pin !== "left") {
      continue;
    }
    map.set(column.id, { side: "left", offset: leftOffset, edge: false });
    leftOffset += pinnedColumnWidth(meta.colWidth);
    lastLeftId = column.id;
  }
  if (lastLeftId) {
    map.get(lastLeftId)!.edge = true;
  }

  let rightOffset = 0;
  let lastRightId: string | null = null;
  for (let index = columns.length - 1; index >= 0; index -= 1) {
    const column = columns[index];
    const meta = getColumnMeta(column.meta);
    if (meta?.pin !== "right") {
      continue;
    }
    map.set(column.id, { side: "right", offset: rightOffset, edge: false });
    rightOffset += pinnedColumnWidth(meta.colWidth);
    lastRightId = column.id;
  }
  if (lastRightId) {
    map.get(lastRightId)!.edge = true;
  }

  return map;
}

export function dataTableMeta(
  colWidth: DataTableColWidth,
  extra?: Omit<DataTableColumnMeta, "colWidth">,
): DataTableColumnMeta {
  return { colWidth, ...extra };
}

export function getColumnMeta(meta: unknown): DataTableColumnMeta | undefined {
  return (meta ?? undefined) as DataTableColumnMeta | undefined;
}

export function sumColumnMinWidths(
  columns: Array<{ meta?: unknown }>,
): number {
  let sum = 0;

  for (const column of columns) {
    const def = getColumnMeta(column.meta)?.colWidth;
    if (!def) {
      continue;
    }

    sum += def.minWidth ?? def.width;
  }

  return sum;
}
